import { useEffect, useId, useRef } from 'react';

import { useStreamChannelStore } from '../stores/useStreamChannelStore';
import { EventBatcher } from '../utils';

import type { StreamEventPayload } from '../constants/types/stream';

type StreamSubscribeFlushCallback = (events: StreamEventPayload[]) => void;

interface UseStreamChannelOptions {
  flushInterval?: number;
  onMessageFlush?: StreamSubscribeFlushCallback;
}

/**
 * 스트림 이벤트를 배치로 구독하는 훅입니다.
 * @description
 * - stream 채널에 구독하고 flushInterval마다 이벤트를 전달합니다.
 * - useId로 구독 키를 만들고 언마운트 시 자동 해제합니다.
 * @example
 * useStreamChannel({
 *   flushInterval: 3000,
 *   onMessageFlush: events => {
 *     console.log(events);
 *   },
 * });
 */
const useStreamChannel = (options: UseStreamChannelOptions = {}) => {
  const { onMessageFlush, flushInterval = 3000 } = options;
  const subscribe = useStreamChannelStore(state => state.subscribe);
  const unsubscribe = useStreamChannelStore(state => state.unsubscribe);
  const onMessageFlushRef = useRef(onMessageFlush);
  const batcherRef = useRef<EventBatcher<StreamEventPayload> | null>(null);
  const id = useId();

  useEffect(() => {
    onMessageFlushRef.current = onMessageFlush;
  }, [onMessageFlush]);

  useEffect(() => {
    if (onMessageFlushRef.current && !batcherRef.current) {
      batcherRef.current = new EventBatcher({
        flushInterval,
        onFlush: events => {
          onMessageFlushRef.current?.(events);
        },
      });
    }

    subscribe(id, payload => {
      batcherRef.current?.add(payload);
    });

    return () => {
      unsubscribe(id);
      batcherRef.current?.destroy();
      batcherRef.current = null;
    };
  }, [subscribe, unsubscribe, flushInterval, id]);

  return {
    subscribe,
    unsubscribe,
  };
};

export default useStreamChannel;
